// COMBAT V3 — the sigil set. Pure data + pure selection; imported by the engine
// (to score a received stroke) and by the presentation (to draw the guide).
//
// Every sigil is ONE continuous stroke on a 0..100 board, starting at its first
// point (the guide shows a start dot and an arrow). v11: selection is a pure
// function of (seed, round, side, tier), never of rng — a fresh shape every
// round (Jack: "much broader variety"), on a difficulty ladder: SLASH short and
// simple, RUNE medium, GLYPH hard, ULT hardest.

import { canonicalize } from './score.js';

// Tier numbers are the engine's: 1 SLASH, 2 RUNE, 3 GLYPH, 4 ULT.
export const TIER = Object.freeze({ SLASH: 1, RUNE: 2, GLYPH: 3, ULT: 4 });
export const TIER_NAME = Object.freeze({ 1: 'SLASH', 2: 'RUNE', 3: 'GLYPH', 4: 'ULT' });
// v11 TIME — ENGINE NUMBERS (src/sigil/rules.js timePct reads them). The clock
// runs from the moment the guide appears. Each sigil gets its own PAR from its
// length and corners (sigilTiming below), because a heptagram is twice the ink
// of a heart even though both are ULTs. Finish before par for a speed bonus,
// after it for a growing penalty; at the LIMIT the trace ends with a big
// penalty. Calibrated by scripts/sigil-ev-sim.mjs: a typical hand finishes at
// ~0.85-0.9x par, a slow one near 1.2x, and the limit is 2x par + 1s.
export const TIME_READ_MS = 700;       // see the shape, find the start dot
export const TIME_PER_UNIT_MS = 8;     // per board unit of path (board = 100)
export const TIME_PER_TURN_MS = 120;   // per corner sharper than 60 degrees
export const TIME_FAST_PCT = 60;       // full speed bonus at <= 60% of par
export const TIME_LIMIT_SLACK_MS = 1000;
// How long the guide stays fully lit in MEMORY mode before it vanishes.
export const MEMORY_FLASH_MS = 1000;

const S = (id, name, pts) => Object.freeze({ id, name, pts: Object.freeze(pts.map(p => Object.freeze(p))) });

// Points are LITERAL integers on purpose: templates must be bit-identical on
// every client, and Math.sin/cos are not correctly rounded across engines.
// (The curves were generated offline, then frozen here.)
// SLASH — one short, simple stroke: a line, an arc, one bend, one wave.
export const SLASHES = Object.freeze([
  S('cleave', 'CLEAVE', [[10, 10], [90, 90]]),
  S('rising', 'RISING', [[10, 90], [90, 10]]),
  S('sweep', 'SWEEP', [[5, 50], [95, 50]]),
  S('drop', 'DROP', [[50, 5], [50, 95]]),
  S('crescent', 'CRESCENT', [[12, 40], [18, 54], [26, 65], [38, 73], [50, 75], [62, 73], [74, 65], [82, 54], [88, 40]]),
  S('moon', 'MOON', [[72, 6], [57, 9], [44, 19], [35, 33], [32, 50], [35, 67], [44, 81], [57, 91], [72, 94]]),
  S('peak', 'PEAK', [[10, 85], [50, 15], [90, 85]]),
  S('hook', 'HOOK', [[62, 5], [62, 62], [60, 73], [53, 81], [44, 84], [35, 81], [28, 73], [26, 62]]),
  S('ripple', 'RIPPLE', [[5, 50], [13, 35], [20, 24], [28, 20], [35, 24], [43, 35], [50, 50], [58, 65], [65, 76], [73, 80], [80, 76], [88, 65], [95, 50]]),
]);

// RUNE — medium: 2-4 corners, one closed loop, or a double wave.
export const RUNES = Object.freeze([
  S('fang', 'FANG', [[10, 10], [50, 90], [90, 10]]),
  S('bolt', 'BOLT', [[65, 5], [30, 50], [70, 50], [35, 95]]),
  S('zed', 'ZED', [[10, 10], [90, 10], [10, 90], [90, 90]]),
  S('delta', 'DELTA', [[50, 8], [92, 88], [8, 88], [50, 8]]),
  S('check', 'VERDICT', [[8, 52], [36, 86], [92, 12]]),
  S('orb', 'ORB', [[50, 6], [64, 8], [76, 14], [86, 24], [92, 36], [94, 50], [92, 64], [86, 76], [76, 86], [64, 92], [50, 94], [36, 92], [24, 86], [14, 76], [8, 64], [6, 50], [8, 36], [14, 24], [24, 14], [36, 8], [50, 6]]),
  S('wave', 'WAVE', [[4, 50], [9, 36], [13, 27], [18, 27], [22, 36], [27, 50], [32, 64], [36, 73], [41, 73], [45, 64], [50, 50], [55, 36], [59, 27], [64, 27], [68, 36], [73, 50], [78, 64], [82, 73], [87, 73], [91, 64], [96, 50]]),
  S('zigzag', 'ZIGZAG', [[5, 15], [27, 85], [50, 30], [73, 85], [95, 15]]),
  S('swan', 'SWAN', [[77, 18], [68, 10], [56, 5], [42, 6], [31, 11], [23, 19], [20, 29], [24, 39], [32, 47], [63, 53], [74, 60], [79, 69], [79, 79], [73, 89], [63, 95], [49, 97], [36, 94]]),
]);

// GLYPH — hard: many corners, crossings, loops; the stroke order matters.
export const GLYPHS = Object.freeze([
  S('star', 'STAR', [[50, 4], [79, 94], [3, 38], [97, 38], [21, 94], [50, 4]]),
  S('spiral', 'VORTEX', [[56, 50], [57, 54], [55, 58], [50, 62], [43, 62], [37, 58], [33, 50], [33, 40], [39, 32], [50, 27], [63, 28], [73, 37], [79, 50], [77, 65], [66, 78], [50, 85], [32, 82], [17, 69], [10, 50], [13, 29], [28, 12], [50, 4]]),
  S('infinity', 'INFINITY', [[96, 50], [92, 61], [82, 66], [72, 65], [63, 61], [56, 56], [50, 50], [44, 44], [37, 39], [28, 35], [18, 34], [8, 39], [4, 50], [8, 61], [18, 66], [28, 65], [37, 61], [44, 56], [50, 50], [56, 44], [63, 39], [72, 35], [82, 34], [92, 39], [96, 50]]),
  S('crown', 'CROWN', [[5, 90], [5, 15], [30, 60], [50, 10], [70, 60], [95, 15], [95, 90]]),
  S('serpent', 'SERPENT', [[8, 50], [12, 28], [16, 14], [21, 14], [25, 28], [29, 50], [33, 72], [37, 86], [42, 86], [46, 72], [50, 50], [54, 28], [58, 14], [63, 14], [67, 28], [71, 50], [75, 72], [79, 86], [84, 86], [88, 72], [92, 50]]),
  S('coil', 'COIL', [[14, 80], [10, 76], [8, 65], [9, 50], [12, 35], [19, 24], [27, 20], [35, 24], [42, 35], [46, 50], [46, 65], [44, 76], [40, 80], [36, 76], [34, 65], [35, 50], [38, 35], [45, 24], [53, 20], [61, 24], [68, 35], [72, 50], [72, 65], [70, 76], [66, 80], [62, 76], [60, 65], [61, 50], [64, 35], [71, 24], [79, 20], [87, 24], [94, 35], [97, 50], [98, 65], [96, 76], [92, 80]]),
  S('trefoil', 'TREFOIL', [[96, 50], [94, 54], [89, 57], [81, 58], [72, 58], [61, 55], [50, 50], [40, 43], [32, 35], [27, 27], [24, 19], [25, 14], [27, 10], [31, 10], [36, 13], [42, 19], [46, 27], [49, 38], [50, 50], [49, 62], [46, 73], [42, 81], [36, 87], [31, 90], [27, 90], [25, 86], [24, 81], [27, 73], [32, 65], [40, 57], [50, 50], [61, 45], [72, 42], [81, 42], [89, 43], [94, 46], [96, 50]]),
  S('pulse', 'PULSE', [[4, 58], [24, 58], [31, 44], [37, 58], [44, 58], [52, 8], [60, 92], [67, 58], [76, 58], [82, 48], [88, 58], [96, 58]]),
  S('storm', 'STORM', [[30, 5], [72, 20], [30, 35], [72, 50], [30, 65], [72, 80], [30, 95]]),
]);

// The ULT pool — the hardest strokes in the game. The six type CRESTS keep
// their names (HUD art, docs) and two long double-curves join them.
export const CRESTS = Object.freeze({
  IRON: S('bastion', 'BASTION', [[10, 10], [90, 10], [90, 90], [10, 90], [10, 28], [72, 28], [72, 72], [28, 72], [28, 46], [54, 46]]),
  VOYAGE: S('tide', 'TIDE', [[5, 60], [10, 45], [15, 34], [20, 30], [25, 34], [30, 45], [35, 60], [40, 75], [45, 86], [50, 90], [55, 86], [60, 75], [65, 60], [70, 45], [75, 34], [80, 30], [85, 34], [90, 45], [95, 92], [80, 92], [65, 92], [50, 92], [35, 92], [20, 92], [5, 92]]),
  HUSTLE: S('thunder', 'THUNDER', [[20, 5], [5, 45], [45, 45], [25, 95], [75, 40], [40, 40], [60, 5]]),
  MYSTIC: S('hepta', 'HEPTAGRAM', [[50, 6], [70, 93], [14, 23], [95, 62], [5, 62], [86, 23], [30, 93], [50, 6]]),
  CHAOS: S('rift', 'RIFT', [[96, 50], [69, 69], [49, 92], [32, 67], [11, 48], [35, 33], [53, 15], [66, 37], [82, 54], [61, 64], [46, 78], [37, 59]]),
  HEART: S('heart', 'HEART', [[50, 31], [51, 27], [56, 19], [66, 13], [79, 13], [90, 21], [95, 34], [90, 48], [79, 60], [66, 72], [56, 82], [51, 90], [50, 93], [49, 90], [44, 82], [34, 72], [21, 60], [10, 48], [5, 34], [10, 21], [21, 13], [34, 13], [44, 19], [49, 27], [50, 31]]),
});
export const ULTS = Object.freeze([
  ...Object.values(CRESTS),
  S('galaxy', 'GALAXY', [[31, 50], [32, 52], [31, 55], [27, 57], [22, 57], [18, 53], [16, 47], [18, 40], [25, 35], [33, 34], [42, 39], [47, 48], [47, 59], [40, 69], [28, 74], [72, 26], [84, 31], [91, 41], [91, 52], [86, 61], [77, 66], [69, 65], [62, 60], [60, 53], [62, 47], [66, 43], [71, 43], [75, 45], [76, 48], [75, 50]]),
  S('clover', 'CLOVER', [[96, 50], [93, 57], [85, 62], [74, 62], [62, 58], [50, 50], [42, 39], [38, 26], [39, 15], [43, 7], [50, 4], [57, 7], [62, 15], [62, 26], [58, 38], [50, 50], [39, 58], [26, 62], [15, 62], [7, 57], [4, 50], [7, 43], [15, 38], [26, 38], [38, 42], [50, 50], [58, 61], [62, 74], [62, 85], [57, 93], [50, 96], [43, 93], [39, 85], [38, 74], [42, 62], [50, 50], [61, 42], [74, 38], [85, 39], [93, 43], [96, 50]]),
]);

export const POOLS = Object.freeze({ 1: SLASHES, 2: RUNES, 3: GLYPHS, 4: ULTS });

// FNV-1a over the id's UTF-16 code units — integer-only, identical everywhere.
export function idHash(id) {
  let h = 2166136261 >>> 0;
  const s = String(id || '');
  for (let i = 0; i < s.length; i++) { h ^= s.charCodeAt(i); h = Math.imul(h, 16777619) >>> 0; }
  return h >>> 0;
}

/**
 * v11: the sigil a side draws this round for a tier — a pure integer hash of
 * (battle seed, round, side, tier). Never touches st.rng, so the engine (to
 * score the stroke) and the presentation (to draw the guide and the button)
 * agree without either consuming randomness. Both sides see each other's.
 */
export function sigilForRound(seed, round, side, tier) {
  const pool = POOLS[tier] || SLASHES;
  let h = 2166136261 >>> 0;
  for (const v of [seed | 0, round | 0, side === 'B' ? 2 : 1, tier | 0]) {
    h ^= v & 0xffff; h = Math.imul(h, 16777619) >>> 0;
    h ^= (v >>> 16) & 0xffff; h = Math.imul(h, 16777619) >>> 0;
  }
  h ^= h >>> 13; h = Math.imul(h, 0x5bd1e995) >>> 0; h ^= h >>> 15;
  return pool[(h >>> 0) % pool.length];
}

// Canonical template bytes, computed once per sigil and memoised. Only exact
// IEEE operations run here (see score.js), so every client gets the same bytes.
const canonCache = new Map();
export function templateBytes(sigil) {
  let b = canonCache.get(sigil.id);
  if (!b) {
    b = canonicalize(sigil.pts.map(([x, y]) => ({ x, y })));
    canonCache.set(sigil.id, b);
  }
  return b;
}

export function allSigils() {
  return [...SLASHES, ...RUNES, ...GLYPHS, ...ULTS];
}

/**
 * v11: { len, turns, par, fast, limit } for a sigil, in board units and ms.
 * Integer-only apart from Math.sqrt (correctly rounded by spec), so every
 * client derives identical numbers from the literal points.
 */
const timingCache = new Map();
export function sigilTiming(sigil) {
  let t = timingCache.get(sigil.id);
  if (t) return t;
  const P = sigil.pts;
  let len = 0, turns = 0;
  for (let i = 1; i < P.length; i++) {
    const ax = P[i][0] - P[i - 1][0], ay = P[i][1] - P[i - 1][1];
    len += Math.sqrt(ax * ax + ay * ay);
    if (i + 1 < P.length) {
      const bx = P[i + 1][0] - P[i][0], by = P[i + 1][1] - P[i][1];
      const dot = ax * bx + ay * by;
      if (dot < 0 || 4 * dot * dot < (ax * ax + ay * ay) * (bx * bx + by * by)) turns++;
    }
  }
  len = Math.floor(len);
  const par = Math.ceil((TIME_READ_MS + len * TIME_PER_UNIT_MS + turns * TIME_PER_TURN_MS) / 100) * 100;
  t = Object.freeze({ len, turns, par, fast: Math.floor((par * TIME_FAST_PCT) / 100), limit: par * 2 + TIME_LIMIT_SLACK_MS });
  timingCache.set(sigil.id, t);
  return t;
}
// The longest limit per tier — the MP turn-timer budget (src/combat3.js).
export const TRACE_MS = Object.freeze(Object.fromEntries(Object.entries(POOLS)
  .map(([tier, pool]) => [tier, Math.max(...pool.map(sg => sigilTiming(sg).limit))])));
